import React, { useState, useEffect, useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography, Button } from '@mui/material';
import Comment from './comment';
import { useAuth } from '../../hooks/useAuth';
import { postComment, getGroupComments } from '../../services/group-services';
import { CssTextField } from '../layout/elements';

/* ------------------------------------------------------------------ *
 * BWF · Comments — branded composer + paginated comment list.
 * Comments are fetched from the backend in pages to keep the group
 * payload small: 5 on first load, +10 per "Load more".
 * ------------------------------------------------------------------ */

const LINE = '1px solid rgba(255,255,255,0.08)';
const PAGE_INITIAL = 5;
const PAGE_STEP = 10;

// Left-aligned multiline variant of the app's orange text field.
const CommentField = styled(CssTextField)({
  '& .MuiInputBase-input': { textAlign: 'left', fontSize: '0.95rem', color: '#fff' },
  '& .MuiOutlinedInput-root': { borderRadius: 10, background: '#161616' },
});

function Comments({ group, onSeen }) {
  const { authData } = useAuth();
  const [newComment, setNewComment] = useState('');
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const getUser = (userId) => group.members.find((m) => m.user.id === userId)?.user;

  const markSeen = useCallback((latestId) => {
    if (latestId) {
      localStorage.setItem(`lastSeenComment_${group.id}_${authData.user.id}`, latestId);
    }
    if (onSeen) onSeen();
  }, [group.id, authData.user.id, onSeen]);

  // Initial page load
  useEffect(() => {
    let active = true;
    setLoading(true);
    getGroupComments(authData.token, group.id, { limit: PAGE_INITIAL, offset: 0 })
      .then((data) => {
        if (!active) return;
        setComments(data.results);
        setTotal(data.count);
        // Opening the comments tab counts as "seeing" the latest comment
        markSeen(data.latest_id);
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [authData.token, group.id, markSeen]);

  const loadMore = () => {
    setLoadingMore(true);
    getGroupComments(authData.token, group.id, { limit: PAGE_STEP, offset: comments.length })
      .then((data) => {
        setComments((prev) => [...prev, ...data.results]);
        setTotal(data.count);
      })
      .finally(() => setLoadingMore(false));
  };

  const sendComment = () => {
    postComment(authData.token, newComment, group.id, authData.user.id).then((resp) => {
      setNewComment('');
      setComments((prev) => [resp, ...prev]);
      setTotal((t) => t + 1);
      markSeen(resp.id);
    });
  };

  const remaining = total - comments.length;

  return (
    <Box sx={{ textAlign: 'left' }}>
      {/* composer */}
      <Box sx={{ background: '#1e1e1e', border: LINE, borderRadius: '14px', p: '14px', mb: 2.75 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: '#b0b0b0', mb: 1.25 }}>
          Add a comment
        </Typography>
        <CommentField
          multiline
          minRows={2}
          fullWidth
          variant="outlined"
          placeholder="Share your take on the match…"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
          <Button onClick={sendComment} disabled={!newComment} variant="contained" color="primary">
            Post comment
          </Button>
        </Box>
      </Box>

      {/* list */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.75, px: 0.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Comments</Typography>
        <Typography sx={{ fontSize: 11, color: '#b0b0b0' }}>{total}</Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
        {comments.map((comment) => (
          <Comment
            key={comment.id ?? `${comment.user}-${comment.time}`}
            comment={comment}
            user={getUser(comment.user)}
            isMe={comment.user === authData.user.id}
          />
        ))}
        {loading && (
          <Typography sx={{ fontSize: 13, color: '#7a7a7a', textAlign: 'center', py: 2 }}>
            Loading…
          </Typography>
        )}
        {!loading && total === 0 && (
          <Typography sx={{ fontSize: 13, color: '#7a7a7a', textAlign: 'center', py: 2 }}>
            No comments yet — start the conversation.
          </Typography>
        )}
      </Box>

      {remaining > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button
            onClick={loadMore}
            disabled={loadingMore}
            variant="outlined"
            sx={{ textTransform: 'none', color: '#ff9800', borderColor: 'rgba(255,152,0,0.4)', '&:hover': { borderColor: '#ff9800', background: 'rgba(255,152,0,0.08)' } }}
          >
            {loadingMore ? 'Loading…' : `Load ${Math.min(PAGE_STEP, remaining)} more`}
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default Comments;
